// Shared API utilities
import { SanctumService } from './sanctumService';
import { ApiError } from '../errors/ApiError';
import { ApiResponse, LaravelErrorResponse } from '../types';

/**
 * Check if the current user is authenticated.
 * Uses the global window state set by useUser hook.
 */
export function isAuthenticated(): boolean {
  // window.__USER_DATA__ is declared in useUser.ts
  return !!(window as { __USER_DATA__?: unknown }).__USER_DATA__;
}

/**
 * Returns '/guest' for unauthenticated users, '' for authenticated users.
 * Used to build API paths: `/api/v2${guestPrefix()}/endpoint`
 */
export function guestPrefix(): string {
  return isAuthenticated() ? '' : '/guest';
}

export interface FetchOptions {
  unwrapApiResponse?: boolean;
  handle422Errors?: boolean;
}

export async function fetchWithErrorHandling<T>(
  url: string,
  requestOptions?: RequestInit,
  options?: FetchOptions
): Promise<T> {
  try {
    const response = await fetch(url, SanctumService.getFetchOptions(requestOptions));

    if (!response.ok) {
      // Handle Laravel 422 validation errors if requested
      if (response.status === 422 && options?.handle422Errors) {
        const errorData = await response.json() as LaravelErrorResponse;
        throw new ApiError(errorData.message, 422, errorData.errors);
      }

      // Try to extract an error message from the response body
      try {
        const errorBody = await response.json();
        if (errorBody.error) {
          throw new ApiError(errorBody.error, response.status);
        }
        if (errorBody.message) {
          throw new ApiError(errorBody.message, response.status);
        }
      } catch (e) {
        if (e instanceof ApiError) throw e;
      }

      throw new ApiError(`HTTP error! status: ${response.status}`, response.status);
    }

    const result = await response.json();

    // Unwrap ApiResponse wrapper if requested
    if (options?.unwrapApiResponse) {
      const apiResponse = result as ApiResponse<T>;
      if (!apiResponse.success) {
        throw new Error(apiResponse.message || 'API request failed');
      }
      return apiResponse.data;
    }

    return result;
  } catch (error: unknown) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    console.error('API request failed:', error);
    throw error;
  }
}
